import React, { use, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";

import { CommonDashboardWrapper } from "styles/GlobalStyles";
import { useCreatorUsersSlice } from "../slice";
import { getBrandFollowers } from "../slice/selectors";
import CommonTable from "components/Tables/CommonTable";
import brandColumns from "./brandColumns";
import LowFollowerReviewModal from "./LowFollowerReviewModal";
import {
  CardFlexWrapperStyles,
  CardWrapperStyles,
  TableWrapperStyles
} from "pages/TalentUploadRequest/styled";
import { statusOptions } from "./data";
import Icon from "components/Icon/Icon";
import { DotIcon } from "@phosphor-icons/react";
import PillButtons from "components/Buttons/PillButtons";
import { RightIconOutlineRegularIcon } from "assets/SVGComponents";

const BrandFollowers = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { actions } = useCreatorUsersSlice();
  const { loading, logs, pagination, total, status_counts } =
    useSelector(getBrandFollowers);

  const page = Number(searchParams.get("page")) || 1;
  const activeStatus = searchParams.get("status") || "pending_approval";

  const [showModal, setShowModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  useEffect(() => {
    if (!searchParams.get("status")) {
      const params = new URLSearchParams(searchParams);
      params.set("status", "pending_approval");
      setSearchParams(params, { replace: true }); // replace so back button works
    }
  }, []);

  useEffect(() => {
    dispatch(
      actions.fetchBrandFollowersRequest({
        page,
        limit: 10,
        status: activeStatus
      })
    );
  }, [dispatch, actions, page, activeStatus]);

  const handlePageChange = (newPage) => {
    const params = new URLSearchParams(searchParams);
    params.set("page", newPage);
    setSearchParams(params);
  };

  const handleStatusSelect = (item) => {
    const params = new URLSearchParams(searchParams);
    if (item.value) {
      params.set("status", item.value);
    }
    params.set("page", 1);
    setSearchParams(params);
  };

  const handleStatusClear = () => {
    const params = new URLSearchParams(searchParams);
    params.delete("status");
    params.set("page", 1);
    setSearchParams(params);
  };

  const handleEdit = (row) => {
    setSelectedRow(row);
    setShowModal(true);
  };

  const columns = brandColumns(handleEdit);
  const tableColumns = useMemo(() => columns, [handleEdit]);

  const handleHide = () => {
    setShowModal(false);
    setSelectedRow(null);
  };

  const activeStatusLabel = useMemo(() => {
    return statusOptions.find((o) => o.value === activeStatus)?.label || "";
  }, [activeStatus]);

  const statusMenuItems = useMemo(() => {
    return statusOptions.map((option) => ({
      label: option.label,
      value: option.value,
      count: status_counts?.[option.value] ?? 0
    }));
  }, [statusOptions, status_counts]);

  const refetchList = () => {
    dispatch(
      actions.fetchBrandFollowersRequest({
        page,
        limit: 10,
        status: activeStatus
      })
    );
  };

  return (
    <>
      <CommonDashboardWrapper>
        <div className="common-dashboard-wrapper">
          <h1 className="common-heading">Review Brand Followers</h1>
        </div>

        <CardFlexWrapperStyles>
          {statusOptions.map((option) => (
            <CardWrapperStyles key={option.label}>
              <div className="card-list">
                <Icon
                  color={option.color}
                  component={DotIcon}
                  weight="fill"
                  format="outline"
                />
                <span className="card-title">{option.label}</span>
              </div>
              <p className="card-count">{status_counts[option.value]}</p>
            </CardWrapperStyles>
          ))}
        </CardFlexWrapperStyles>

        <TableWrapperStyles>
          <div className="filter-bar">
            <div className="filter-bar-menus">
              <PillButtons
                placeholderText="Status"
                mainLabelText={activeStatusLabel}
                menuItems={statusMenuItems}
                rightIcon={<RightIconOutlineRegularIcon />}
                onSelect={handleStatusSelect}
                onClear={handleStatusClear}
                size="M"
                maxWidth="max-content"
                customClassName="status-width"
              />
            </div>
          </div>
        </TableWrapperStyles>
      </CommonDashboardWrapper>

      <CommonTable
        data={logs}
        columns={tableColumns}
        total_count={total}
        loading={loading}
        initialLoading={loading}
        page={page}
        limit={pagination?.limit || 10}
        tableClassName="table-full-view table-striped"
        tableWrapperStyles="table-wrapper-styles"
        onPageChange={handlePageChange}
      />

      <LowFollowerReviewModal
        show={showModal}
        rowData={selectedRow}
        type={null}
        onHide={handleHide}
        onActionSuccess={refetchList}
      />
    </>
  );
};

export default BrandFollowers;
